import * as React from 'react';

import { DocumentType } from '@rematter/frontend-lib-api-client';
import { useToast } from '@rematter/ui-web/hooks';
import { useSetAtom } from 'jotai';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { createMutationAtomFactory } from '~/shared/providers/api/createMutationAtomFactory';

export interface DocumentInput {
  documentType: DocumentType;
  loadId?: string;
  bookingId?: string;
  purchaseOrderId?: string;
  invoiceNumber?: string;
  invoiceDate?: string;
  dueDate?: string;
}

export interface EmailInput {
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  replyTo?: string;
  textBody?: string;
  htmlBody?: string;
  templateId?: string;
  templateData?: Array<{ key: string; value: string }>;
}

export interface GenerateDocumentsAndSendEmailParams {
  documents: DocumentInput[];
  email: EmailInput;
}

export const generateDocumentsAndSendEmailMutationAtom =
  createMutationAtomFactory(
    'trade.tradeGenerateDocumentsAndSendEmail',
    (params: GenerateDocumentsAndSendEmailParams) => ({
      tradeGenerateDocumentsAndSendEmail: {
        __args: {
          input: {
            documents: params.documents,
            email: params.email,
          },
        },
        documents: {
          documentType: true,
          success: true,
          fileId: true,
          workflowId: true,
          errorMessage: true,
        },
        email: {
          success: true,
          messageId: true,
          workflowId: true,
          errorMessage: true,
        },
      },
    }),
  );

const $generateDocumentsAndSendEmail =
  generateDocumentsAndSendEmailMutationAtom();

export const useGenerateDocumentsAndSendEmail = () => {
  const toast = useToast();
  const t = useTranslations();
  const [isPending, setIsPending] = React.useState(false);
  const generateDocumentsAndSendEmail = useSetAtom(
    $generateDocumentsAndSendEmail,
  );

  const sendDocumentsAndEmail = async (
    params: GenerateDocumentsAndSendEmailParams,
  ) => {
    try {
      setIsPending(true);
      const result = await generateDocumentsAndSendEmail(params);

      // Check document generation results
      const successfulDocuments =
        result.tradeGenerateDocumentsAndSendEmail?.documents?.filter(
          (doc) => doc.success,
        ) ?? [];
      const failedDocuments =
        result.tradeGenerateDocumentsAndSendEmail?.documents?.filter(
          (doc) => !doc.success,
        ) ?? [];

      // Check email sending result
      const emailResult = result.tradeGenerateDocumentsAndSendEmail?.email;

      // Show appropriate messages
      if (successfulDocuments.length > 0) {
        toast.success(t.documents.email.success(successfulDocuments.length));
      }

      if (failedDocuments.length > 0) {
        toast.error(t.documents.email.partialFailure(failedDocuments.length));
      }

      if (emailResult?.success) {
        toast.success(t.documents.email.sent);
      } else if (emailResult?.errorMessage) {
        toast.error(t.documents.email.failed(emailResult.errorMessage));
      }

      return {
        success: emailResult?.success ?? false,
        documents: result.tradeGenerateDocumentsAndSendEmail?.documents ?? [],
        email: emailResult,
      };
    } catch (error) {
      console.error('Failed to generate documents and send email:', error);
      toast.error(t.documents.email.error);
      throw error;
    } finally {
      setIsPending(false);
    }
  };

  return {
    sendDocumentsAndEmail,
    isPending,
  };
};
